import React from 'react'
import { Box, Typography, Button, CircularProgress, Alert, Paper } from '@mui/material'
import ReactMarkdown from 'react-markdown'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

export type PreviewState =
  | { kind: 'deferred' } // dashboard not saved yet — preview runs after Accept + Save
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; report: string }
  | { kind: 'error'; message: string }
  // #1105: the server refused the create (ownership today, the "can use AI
  // tokens" permission once #1219 lands). Not a failed preview: nothing was
  // created, Retry cannot succeed, and Accept must not append a card.
  | { kind: 'refused'; message: string }

export interface WizardStep3PreviewProps {
  state: PreviewState
  onGeneratePreview: () => void
  onRefine: () => void
  onAccept: () => void
  accepting: boolean
}

/** Step 3 — preview + refine (#970 Part A). */
export const WizardStep3Preview: React.FC<WizardStep3PreviewProps> = ({ state, onGeneratePreview, onRefine, onAccept, accepting }) => {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 640, mx: 'auto', width: '100%' }}>
      <Typography variant="h6">Does this look right?</Typography>

      {state.kind === 'deferred' && (
        <Alert severity="info">
          This dashboard isn&apos;t saved yet, so a live preview isn&apos;t available. The analysis will run
          automatically as soon as you accept and save.
        </Alert>
      )}

      {state.kind === 'idle' && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate a preview to see the AI output before saving.
          </Typography>
          <Button variant="contained" color="info" onClick={onGeneratePreview}>
            Generate preview
          </Button>
        </Box>
      )}

      {state.kind === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">Generating preview…</Typography>
        </Box>
      )}

      {state.kind === 'error' && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={onGeneratePreview}>Retry</Button>}>
          {state.message}
        </Alert>
      )}

      {state.kind === 'refused' && (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            You can&apos;t add AI analyses to this dashboard.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{state.message}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Use &quot;Save to My dashboards&quot; to add cards to your own copy.
          </Typography>
        </Alert>
      )}

      {state.kind === 'ready' && (
        <Paper variant="outlined" sx={{ p: 2, maxHeight: 360, overflow: 'auto' }}>
          <Box sx={{ '& p': { fontSize: '0.85rem' }, '& ul, & ol': { pl: 2 } }}>
            <ReactMarkdown>{state.report}</ReactMarkdown>
          </Box>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 'auto' }}>
        {state.kind === 'ready' && (
          <Button variant="outlined" color="info" startIcon={<RefreshIcon />} onClick={onRefine}>
            Refine
          </Button>
        )}
        <Button
          variant="contained"
          color="info"
          startIcon={<CheckCircleIcon />}
          disabled={state.kind === 'loading' || state.kind === 'refused' || accepting}
          onClick={onAccept}
        >
          {accepting ? 'Saving…' : 'Accept + Save'}
        </Button>
      </Box>
    </Box>
  )
}

export default WizardStep3Preview
